package pl.nickers.match3.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.BugReport
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.unit.dp
import kotlinproject.composeapp.generated.resources.Res
import kotlinproject.composeapp.generated.resources.parchment_bg
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import pl.nickers.match3.BackHandlerEffect
import pl.nickers.match3.GridPos
import pl.nickers.match3.ContinueContext
import pl.nickers.match3.GameStorage
import pl.nickers.match3.GameViewModel
import pl.nickers.match3.JourneyScreen
import pl.nickers.match3.FamiliarSelectScreen
import pl.nickers.match3.JourneyService
import pl.nickers.match3.EconomyConfig
import pl.nickers.match3.LevelConfig
import pl.nickers.match3.LivesService
import pl.nickers.match3.NoOpGemDebiter
import pl.nickers.match3.formatLivesCountdown
import pl.nickers.match3.ProfileScreen
import pl.nickers.match3.SettingsScreen
import pl.nickers.match3.StartScreen
import pl.nickers.match3.TestLevels
import pl.nickers.match3.TestLevelsScreen
import pl.nickers.match3.WalletService
import pl.nickers.match3.ecs.EntityCatalog
import pl.nickers.match3.pickBackgroundImage

private sealed class RootScreen {
    object Start : RootScreen()
    object Journey : RootScreen()
    object TestLevelsList : RootScreen()
    object Profile : RootScreen()
    object FamiliarSelect : RootScreen()
    object Settings : RootScreen()
    data class Game(val forceNewGame: Boolean) : RootScreen()
    data class PlayLevel(val levelId: Int) : RootScreen()
    data class PlayTestLevel(val levelId: Int) : RootScreen()
}

@Composable
internal fun AppScreen() {
    val storage = remember { GameStorage() }
    // The one wallet instance for the whole app: every screen renders its balance
    // and every future spend hook (E17/E19/E20) debits through it (E18/F18.1).
    val wallet = remember { WalletService(storage) }
    var catalog by remember { mutableStateOf<EntityCatalog?>(null) }
    var journeyService by remember { mutableStateOf<JourneyService?>(null) }
    val livesService = remember { LivesService(storage) }
    val gemDebiter = remember { NoOpGemDebiter }
    var livesInfo by remember { mutableStateOf(livesService.info()) }
    var outOfLivesLevelId by remember { mutableStateOf<Int?>(null) }

    fun refreshLives() {
        livesInfo = livesService.info()
    }

    LaunchedEffect(Unit) {
        // Idempotent: pays the starter grant on first launch only.
        wallet.grantStarterGemsIfNeeded()
        catalog = EntityCatalog.load()
        journeyService = JourneyService.create(storage)
    }
    val gemBalance by wallet.balance.collectAsState()
    val gemEarnEvent by wallet.lastEarn.collectAsState()

    val navigationStack = remember { mutableStateListOf<RootScreen>(RootScreen.Start) }
    val currentScreen = navigationStack.last()

    fun navigateTo(screen: RootScreen) {
        navigationStack.add(screen)
    }

    fun tryOpenJourneyLevel(levelId: Int) {
        refreshLives()
        if (livesService.currentLives() <= 0) {
            outOfLivesLevelId = levelId
            refreshLives()
        } else {
            navigateTo(RootScreen.PlayLevel(levelId))
        }
    }

    fun navigateBack() {
        when (val leaving = currentScreen) {
            is RootScreen.Game     -> storage.continueContext = ContinueContext("arcade")
            is RootScreen.PlayLevel -> storage.continueContext = ContinueContext("journey", leaving.levelId)
            else                   -> {}
        }
        if (navigationStack.size > 1) navigationStack.removeLast()
    }

    val hasContinueGame = storage.continueContext?.let { ctx ->
        when (ctx.gameMode) {
            "arcade"  -> storage.hasSavedGame()
            "journey" -> true
            else      -> false
        }
    } ?: false

    BackHandlerEffect(enabled = navigationStack.size > 1) {
        navigateBack()
    }

    if (outOfLivesLevelId != null) {
        AlertDialog(
            onDismissRequest = { outOfLivesLevelId = null },
            title = { Text("Out of lives") },
            text = {
                Text("Next life in ${formatLivesCountdown(livesInfo.millisToNextLife)}")
            },
            confirmButton = {
                Button(
                    enabled = false,
                    onClick = {
                        if (gemDebiter.debit(EconomyConfig.LIFE_REFILL_PRICE)) {
                            livesService.refillFull()
                            refreshLives()
                            val levelId = outOfLivesLevelId
                            outOfLivesLevelId = null
                            if (levelId != null) navigateTo(RootScreen.PlayLevel(levelId))
                        }
                    },
                ) { Text("Refill for ${EconomyConfig.LIFE_REFILL_PRICE} gems") }
            },
            dismissButton = { TextButton(onClick = { outOfLivesLevelId = null }) { Text("Wait") } },
        )
    }

    MaterialTheme(colorScheme = GrimoireColorScheme) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(MaterialTheme.colorScheme.primaryContainer),
        ) {
            // Full-screen parchment page background. Centered and scaled to cover
            // the whole screen while preserving the image's aspect ratio (parts get
            // cropped on mismatched aspect ratios, e.g. mobile portrait/landscape).
            Image(
                painter = painterResource(Res.drawable.parchment_bg),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop,
                alignment = Alignment.Center,
            )
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .safeDrawingPadding(),
                contentAlignment = Alignment.Center,
            ) {
                when (val screen = currentScreen) {
                    is RootScreen.Start -> StartScreen(
                        hasContinueGame = hasContinueGame,
                        livesInfo = livesInfo,
                        gemBalance = gemBalance,
                        onNewGame = {
                            storage.continueContext = null
                            navigateTo(RootScreen.Game(forceNewGame = true))
                        },
                        onContinue = {
                            val ctx = storage.continueContext
                            storage.continueContext = null
                            if (ctx?.gameMode == "journey")
                                navigateTo(RootScreen.PlayLevel(ctx.journeyLevelId!!))
                            else
                                navigateTo(RootScreen.Game(forceNewGame = false))
                        },
                        onJourney = { refreshLives(); navigateTo(RootScreen.Journey) },
                        onTestLevels = { navigateTo(RootScreen.TestLevelsList) },
                        onProfile = { navigateTo(RootScreen.Profile) },
                        onFamiliars = { navigateTo(RootScreen.FamiliarSelect) },
                        onSettings = { navigateTo(RootScreen.Settings) },
                    )
                    is RootScreen.Profile -> ProfileScreen(
                        profile = storage.loadProfile(),
                        onSaveDisplayName = { storage.displayName = it },
                        onBack = { navigateBack() },
                    )
                    is RootScreen.Settings -> SettingsScreen(
                        storage = storage,
                        journeyService = journeyService,
                        onBack = { navigateBack() },
                    )
                    is RootScreen.FamiliarSelect -> FamiliarSelectScreen(
                        selected = storage.selectedFamiliar,
                        onSelect = { storage.selectedFamiliar = it },
                        onBack = { navigateBack() },
                    )
                    is RootScreen.Journey -> {
                        val service = journeyService
                        if (service != null) {
                            JourneyScreen(
                                service = service,
                                livesInfo = livesInfo,
                                gemBalance = gemBalance,
                                onLevelSelected = { id -> tryOpenJourneyLevel(id) },
                                onBack = { navigateBack() },
                            )
                        }
                    }
                    is RootScreen.PlayLevel -> {
                        val service = journeyService
                        val loadedCatalog = catalog
                        if (service != null && loadedCatalog != null) {
                            var levelConfig by remember(screen.levelId) { mutableStateOf<LevelConfig?>(null) }
                            LaunchedEffect(screen.levelId) {
                                levelConfig = service.loadLevel(screen.levelId)
                            }
                            val config = levelConfig
                            if (config != null) {
                                val vm = remember(screen) {
                                    GameViewModel(
                                        catalog = loadedCatalog,
                                        storage = storage,
                                        journeyService = service,
                                        livesService = livesService,
                                        wallet = wallet,
                                        levelConfig = config,
                                    )
                                }
                                GameScreen(
                                    vm = vm,
                                    onWinNextLevel = {
                                        storage.continueContext = null
                                        refreshLives()
                                        navigationStack.removeLast()
                                        tryOpenJourneyLevel(screen.levelId + 1)
                                    },
                                    onWinBackToJourney = {
                                        storage.continueContext = null
                                        refreshLives()
                                        if (navigationStack.size > 1) navigationStack.removeLast()
                                    },
                                    onBackToJourney = { vm.finalizeQuitToJourney(); refreshLives(); navigateBack() },
                                    onLivesChanged = ::refreshLives,
                                )
                            }
                        }
                    }
                    is RootScreen.TestLevelsList -> {
                        TestLevelsScreen(
                            onLevelSelected = { id -> navigateTo(RootScreen.PlayTestLevel(id)) },
                            onBack = { navigateBack() },
                        )
                    }
                    is RootScreen.PlayTestLevel -> {
                        val loadedCatalog = catalog
                        val testLevel = TestLevels.byId(screen.levelId)
                        if (loadedCatalog != null && testLevel != null) {
                            val vm = remember(screen) {
                                GameViewModel(
                                    catalog = loadedCatalog,
                                    storage = storage,
                                    wallet = wallet,
                                    initialGrid = testLevel.grid,
                                    forceNewGame = true,
                                    isTestLevel = true,
                                ).also {
                                    it.prepareAsTestLevel(
                                        moveLimit = testLevel.moveLimit,
                                        teleports = testLevel.teleports,
                                        disableSpawn = testLevel.disableSpawn,
                                        goals = testLevel.goals,
                                        familiar = testLevel.familiar,
                                    )
                                }
                            }
                            GameScreen(
                                vm = vm,
                                onWinNextLevel = {},
                                onWinBackToJourney = { navigateBack() },
                                onBackToJourney = { navigateBack() },
                            )
                        }
                    }
                    is RootScreen.Game -> {
                        val loadedCatalog = catalog
                        if (loadedCatalog != null) {
                            val vm = remember(screen) {
                                GameViewModel(
                                    catalog = loadedCatalog,
                                    storage = storage,
                                    wallet = wallet,
                                    forceNewGame = screen.forceNewGame,
                                )
                            }
                            GameScreen(
                                vm = vm,
                                onWinNextLevel = {},
                                onWinBackToJourney = {},
                                onBackToJourney = { navigateBack() },
                            )
                        }
                    }
                }
                // Earn feedback lives at the app root so a credit is visible on
                // whichever screen the player happens to be on (the starter grant
                // lands on Start, the first-win bonus on the game board).
                GemEarnToast(
                    event = gemEarnEvent,
                    onConsumed = { wallet.consumeEarnFeedback() },
                    modifier = Modifier.align(Alignment.TopCenter).padding(top = 12.dp),
                )
            }
        }
    }
}

@Composable
private fun GameScreen(
    vm: GameViewModel,
    onWinNextLevel: () -> Unit,
    onWinBackToJourney: () -> Unit,
    onBackToJourney: (() -> Unit)? = null,
    onLivesChanged: () -> Unit = {},
) {
    val state = vm.state
    val scope = rememberCoroutineScope()
    var editDialogPos by remember { mutableStateOf<GridPos?>(null) }
    var debugToolbarVisible by remember { mutableStateOf(false) }
    val flightAnchors = remember { FlightAnchorRegistry() }

    Box(Modifier.fillMaxSize()) {
        Column(Modifier.fillMaxSize()) {
            Column(Modifier.weight(1f).fillMaxWidth()) {
                Row(
                    Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (onBackToJourney != null) {
                        TextButton(
                            onClick = onBackToJourney,
                            colors = ButtonDefaults.textButtonColors(contentColor = Color.White),
                            modifier = Modifier.background(
                                Color.Black.copy(alpha = 0.35f),
                                shape = RoundedCornerShape(8.dp),
                            ),
                        ) {
                            Text(if (state.isJourneyMode) "← Journey" else "← Menu")
                        }
                    } else {
                        Spacer(Modifier.weight(0.5f))
                    }
                    Box(Modifier.weight(1f), contentAlignment = Alignment.Center) {
                        GoalsRow(
                            goals = state.goals,
                            moveLimit = state.moveLimit,
                            flightAnchors = flightAnchors,
                        )
                    }
                    Row(
                        Modifier.weight(0.5f),
                        horizontalArrangement = Arrangement.End,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        state.familiar?.let { famInfo ->
                            FamiliarProgressBar(
                                progress = famInfo.progress,
                                threshold = famInfo.threshold,
                                modifier = Modifier.onGloballyPositioned { flightAnchors.familiar = it },
                            )
                        }
                        Spacer(Modifier.width(8.dp))
                        if (vm.hasWallet) {
                            val gems by vm.gemBalance.collectAsState()
                            GemChip(balance = gems)
                            Spacer(Modifier.width(8.dp))
                        }
                        ScoreDisplay(score = state.score)
                    }
                }
                Box(
                    Modifier.weight(1f).fillMaxWidth(),
                    contentAlignment = Alignment.Center,
                ) {
                    GameGrid(
                        state = state,
                        padding = GRID_PADDING,
                        onCellClick = vm::onCellClick,
                        onCellSecondaryAction = { pos -> editDialogPos = pos },
                        onDragSwap = vm::onDragSwap,
                        onRowBombTarget = { pos -> scope.launch { vm.useRowBombPowerUp(pos) } },
                        onTargetedRemoveTarget = { pos -> vm.useTargetedRemovePowerUp(pos.row, pos.col) },
                        onTeleportLinkingTarget = { pos -> vm.confirmTeleportLinkingSource(pos) },
                        onSwapFinished = vm::onSwapAnimationFinished,
                        onFallFinished = vm::onFallAnimationFinished,
                        onFuseFinished = vm::onFuseAnimationFinished,
                        onLaserFinished = vm::onLaserAnimationFinished,
                        onEffectsFinished = vm::onEffectsAnimationFinished,
                        onSwirlVacuumFinished = vm::onSwirlVacuumAnimationFinished,
                        backgroundImageBase64 = state.backgroundImageBase64,
                        flightAnchors = flightAnchors,
                        bombPowerUpFlight = state.bombPowerUpFlight,
                        goalJellyFlights = state.goalJellyFlights,
                        familiarFlights = state.familiarFlights,
                        powerUps = state.powerUps,
                        onClearBombPowerUpFlight = vm::clearBombPowerUpFlight,
                        onDismissGoalJellyFlight = vm::dismissGoalJellyFlight,
                        onDismissFamiliarFlight = vm::dismissFamiliarFlight,
                    )
                    if (vm.showDebugOverlay) {
                        TouchTransparentDebugOverlay(
                            phaseHistory = state.debugInfo.phaseHistory,
                            modifier = Modifier.align(Alignment.TopStart),
                        )
                    }
                }
            }

            PowerUpBar(
                powerUps = state.powerUps,
                enabled = !vm.isAnimating,
                inputMode = state.inputMode,
                onUseBombPowerUp = vm::useBombPowerUp,
                onToggleRowBombPowerUp = vm::toggleRowBombPowerUp,
                onToggleTargetedRemovePowerUp = vm::toggleTargetedRemovePowerUp,
                modifier = Modifier.fillMaxWidth(),
                flightAnchors = flightAnchors,
            )
            if (state.won && !vm.isAnimating) {
                WinDialog(
                    score = state.score,
                    stars = state.starsEarned,
                    isNewStarBest = state.isNewStarBest,
                    isJourneyMode = state.isJourneyMode,
                    isLastJourneyLevel = state.isLastJourneyLevel,
                    isTestLevel = state.isTestLevel,
                    onNewGame = { vm.resetBoard() },
                    onNextLevel = onWinNextLevel,
                    onBackToJourney = onWinBackToJourney,
                )
            }
            if (state.lost && !state.won && !vm.isAnimating) {
                LostDialog(
                    onTryAgain = { vm.finalizeLevelLoss(); onLivesChanged(); vm.resetBoard() },
                )
            }
            val editPos = editDialogPos
            if (editPos != null) {
                val editCell = state.grid.getOrNull(editPos.row)?.getOrNull(editPos.col)
                val editIsHole = editCell?.isHole == true
                val cell = editCell?.takeIf { !it.isEmpty }
                EditEntityDialog(
                    selectedPos = editPos,
                    cell = cell,
                    enabled = !vm.isAnimating,
                    isHole = editIsHole,
                    hasTeleportTarget = vm.hasTeleportTargetAt(editPos),
                    onReplace = { type -> vm.replaceSelectedEntity(type); editDialogPos = null },
                    onDelete = { vm.deleteSelectedEntity(); editDialogPos = null },
                    onSetIceCubeLife = vm::setSelectedIceCubeLife,
                    onSetLaserDirection = vm::setSelectedLaserDirection,
                    onSetSpawnPointVisible = vm::setSpawnPointVisible,
                    onToggleHole = { vm.toggleHole(editPos.row, editPos.col); editDialogPos = null },
                    onBeginTeleportLink = { vm.beginTeleportLinking(editPos); editDialogPos = null },
                    onRemoveTeleportTarget = { vm.removeTeleportTargetAtPos(editPos); editDialogPos = null },
                    onDismiss = { editDialogPos = null },
                )
            }
        }

        val tutorial = state.tutorial
        if (tutorial != null) {
            TutorialOverlay(
                tutorial = tutorial,
                onDismiss = vm::dismissTutorial,
            )
        }

        if (debugToolbarVisible) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.4f))
                    .pointerInput(Unit) {
                        awaitPointerEventScope {
                            while (true) {
                                val event = awaitPointerEvent()
                                event.changes.forEach { it.consume() }
                            }
                        }
                    },
            )
            GameBottomToolbar(
                enabled = !vm.isAnimating,
                selectedPos = state.selected,
                onEditEntity = { editDialogPos = state.selected },
                canUndoMove = vm.canUndoMove,
                canRedoMove = vm.canRedoMove,
                disableNewJellySpawn = state.disableNewJellySpawn,
                hasBackgroundImage = state.backgroundImageBase64 != null,
                onResetBoard = vm::resetBoard,
                onDisableNewJellySpawnChange = vm::setDisableNewJellySpawn,
                onSave = { scope.launch { vm.exportToFile() } },
                onLoad = { scope.launch { vm.importFromFile() } },
                onUndoMove = vm::undoMove,
                onRedoMove = vm::redoMove,
                onSetBackground = {
                    scope.launch {
                        val base64 = pickBackgroundImage()
                        if (base64 != null) vm.setBackgroundImage(base64)
                    }
                },
                onRemoveBackground = vm::removeBackgroundImage,
                onToggleDebugOverlay = vm::toggleDebugOverlay,
                isDebugOverlayVisible = vm.showDebugOverlay,
                onAddPowerUpUse = vm::addPowerUpUse,
                boardWidth = state.width,
                boardHeight = state.height,
                onSetBoardSize = vm::setBoardSize,
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .padding(bottom = 88.dp),
            )
        }

        FloatingActionButton(
            onClick = { debugToolbarVisible = !debugToolbarVisible },
            modifier = Modifier
                .align(Alignment.BottomEnd)
                .padding(16.dp),
        ) {
            Icon(
                imageVector = if (debugToolbarVisible) Icons.Filled.Close else Icons.Filled.BugReport,
                contentDescription = if (debugToolbarVisible) "Hide debug toolbar" else "Show debug toolbar",
            )
        }
    }
}
